import { useState, useEffect, useRef, useCallback } from "react";
import { Droplet, Share2, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCardTilt } from "@/lib/useCardTilt";
import { SnapshotHistoryStrip } from "./SnapshotHistoryStrip";
import { RunwayComparisonOverlay } from "./RunwayComparisonOverlay";
import { SnapshotPreviewDrawer } from "./SnapshotPreviewDrawer";

// ---------- Liquidity Runway Sine Wave — cyan→amber gradient stroke sparkline ----------
// Static planning defaults — used until the live FX fetch completes / if it fails.
const CCY_META = {
  USD: { symbol: "$", fx: 1, rate: 0.37, label: "US Fed top bracket" },
  EUR: { symbol: "€", fx: 0.92, rate: 0.45, label: "DE marg. rate proxy" },
  GBP: { symbol: "£", fx: 0.79, rate: 0.45, label: "UK marg. rate" },
};

export function LiquidityRunwaySineWave({ stats, receipts }) {
  const [hoverIdx, setHoverIdx] = useState(null);
  // Currency Shift — flip the marginal-rate savings estimate between the
  // three currencies our cross-border SMB users tell us they plan against.
  // Marginal rates stay static (top-bracket proxies); FX is fetched live from
  // /api/fx/rates (open.er-api.com behind a 1-hour cache).
  const [liveFx, setLiveFx] = useState(null); // { EUR, GBP } from server
  const [fxUpdatedAt, setFxUpdatedAt] = useState(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const CURRENCIES = {
    USD: { ...CCY_META.USD, fx: 1 },
    EUR: { ...CCY_META.EUR, fx: liveFx?.EUR ?? CCY_META.EUR.fx },
    GBP: { ...CCY_META.GBP, fx: liveFx?.GBP ?? CCY_META.GBP.fx },
  };
  useEffect(() => {
    let alive = true;
    api
      .get("/fx/rates")
      .then((r) => {
        if (!alive) return;
        const rates = r?.data?.rates || {};
        setLiveFx({
          EUR: Number(rates.EUR) || null,
          GBP: Number(rates.GBP) || null,
        });
        setFxUpdatedAt(r?.data?.updated_at || null);
      })
      .catch(() => {
        /* silent — static defaults remain in place */
      });
    return () => {
      alive = false;
    };
  }, []);
  const [ccy, setCcy] = useState(() => {
    try {
      const saved = window.localStorage.getItem("rs.runway.ccy");
      return saved && CURRENCIES[saved] ? saved : "USD";
    } catch (_) {
      return "USD";
    }
  });
  const setCurrency = (code) => {
    setCcy(code);
    try {
      window.localStorage.setItem("rs.runway.ccy", code);
    } catch (_) {
      /* silent */
    }
  };
  // ---- Runway Snapshot Share (signed image link for CPAs) ----
  const [shareUrl, setShareUrl] = useState(null);
  const [shareToken, setShareToken] = useState(null);
  const [shareExpiresAt, setShareExpiresAt] = useState(null);
  const [shareRemaining, setShareRemaining] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailNote, setEmailNote] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [emailSentTo, setEmailSentTo] = useState(null);
  const [emailRemaining, setEmailRemaining] = useState(null);
  const lastComputedSavedRef = useRef(0);
  // ---- Snapshot History strip + Comparison overlay ----
  const [snapshotHistory, setSnapshotHistory] = useState([]);
  const [analytics, setAnalytics] = useState(null); // {total_snapshots,total_copies,total_sends,window_days}
  const [pinA, setPinA] = useState(null); // full snapshot obj
  const [pinB, setPinB] = useState(null);
  const [showComparison, setShowComparison] = useState(false);
  const refreshSnapshotHistory = useCallback(async () => {
    try {
      const r = await api.get("/runway/snapshots");
      setSnapshotHistory(Array.isArray(r?.data?.snapshots) ? r.data.snapshots : []);
    } catch (_) {
      /* silent — strip is a bonus; failure shouldn't block the widget */
    }
  }, []);
  const refreshAnalytics = useCallback(async () => {
    try {
      const r = await api.get("/runway/analytics");
      setAnalytics(r?.data || null);
    } catch (_) {
      /* silent */
    }
  }, []);
  useEffect(() => {
    refreshSnapshotHistory();
    refreshAnalytics();
  }, [refreshSnapshotHistory, refreshAnalytics]);
  const pinSnapshot = (snap, slot) => {
    if (slot === "A") {
      setPinA(snap);
      if (pinB && pinB.token === snap.token) setPinB(null);
    } else {
      setPinB(snap);
      if (pinA && pinA.token === snap.token) setPinA(null);
    }
    toast.message(`Pinned as ${slot}${slot === "A" ? " · pin B next" : " · comparison ready"}`, {
      duration: 2600,
      id: "runway-pin",
    });
  };
  const clearPins = () => {
    setPinA(null);
    setPinB(null);
    setShowComparison(false);
  };
  // Snapshot Restore — reopens the Preview Drawer with a historical signed URL
  // instead of minting a fresh token. Zero API cost, honors the daily rate limit.
  const restoreSnapshot = (s) => {
    if (!s || !s.token) return;
    const full = `${buildShareBaseUrl()}${s.url}`;
    setShareUrl(full);
    setShareToken(s.token);
    setShareExpiresAt(s.expires_at || null);
    setShareRemaining(null); // remaining_today applies only to fresh POSTs
    setEmailRemaining(Math.max(0, 5 - (s.email_count || 0)));
    setEmailNote(s.note || "");
    setEmailSentTo(null);
    setShareCopied(false);
    setShowPreview(true);
    toast.message("Restored — reusing this signed link", {
      duration: 2600,
      id: "runway-restore",
      description: `${5 - (s.email_count || 0)} sends left on this snapshot.`,
    });
  };
  const formatCountdown = (secs) => {
    if (!secs || secs < 0) return "expired";
    const d = Math.floor(secs / 86400);
    const h = Math.floor((secs % 86400) / 3600);
    if (d >= 1) return `${d}d ${h}h`;
    const m = Math.floor((secs % 3600) / 60);
    return `${h}h ${m}m`;
  };
  const buildShareBaseUrl = useCallback(() => {
    // Prefer the backend URL React was built with, fall back to same-origin.
    const base =
      (typeof process !== "undefined" &&
        process.env &&
        process.env.REACT_APP_BACKEND_URL) ||
      "";
    return base.replace(/\/$/, "");
  }, []);
  const buildSharePayload = useCallback(
    (currency, series, forecastVals, headroomVal, marginalSaved) => {
      const active = CURRENCIES[currency] || CURRENCIES.USD;
      return {
        currency,
        symbol: active.symbol,
        marginal_rate: active.rate,
        marginal_savings: Number(marginalSaved.toFixed(2)),
        headroom: Number(headroomVal.toFixed(2)),
        series: series.slice(-12).map((v) => Number(v.toFixed(2))),
        forecast: (forecastVals || []).map((v) => Number(v.toFixed(2))),
        note: `Headroom ${Math.round(headroomVal)}/100 · ${active.symbol}${Math.round(
          marginalSaved
        )} marg. save @ ${Math.round(active.rate * 100)}% (${currency}).`,
      };
    },
    [CURRENCIES]
  );
  // Build 12 weekly runway points: cash headroom = BUDGET_CAP - rolling 4-week burn.
  // No new endpoint — synthesize from the receipts list (safe for demo tenants).
  const BUDGET_CAP = 10000;
  const POINTS = 12;

  // 3D parallax tilt + numeric-change ripple (fires when receipts count changes)
  const { bind: tiltBind, Ripples: TiltRipples } = useCardTilt({ pulseKey: (receipts || []).length });

  // Group receipts by ISO week, sum totals
  const weekMap = new Map();
  (receipts || []).forEach((r) => {
    const iso = (r.date || r.created_at || "").slice(0, 10);
    if (!iso) return;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return;
    // Week key = year + week-of-year (rough Monday-anchored)
    const day = d.getDay() || 7;
    d.setDate(d.getDate() - (day - 1));
    const key = d.toISOString().slice(0, 10);
    weekMap.set(key, (weekMap.get(key) || 0) + Number(r.total || 0));
  });

  const today = new Date();
  const day0 = today.getDay() || 7;
  today.setDate(today.getDate() - (day0 - 1));
  const points = [];
  const weekMeta = []; // parallel array: { weekKey, burn, headroom }
  for (let i = POINTS - 1; i >= 0; i--) {
    const w = new Date(today);
    w.setDate(w.getDate() - 7 * i);
    const key = w.toISOString().slice(0, 10);
    const burn = weekMap.get(key) || 0;
    // Runway (0-100 scale): higher = safer. Add a subtle sine oscillation on top for
    // the "wave" personality regardless of low-signal weeks.
    const base = Math.max(0, Math.min(100, 100 - (burn / BUDGET_CAP) * 400));
    const wave = 6 * Math.sin((i / POINTS) * Math.PI * 2);
    const headroom = Math.max(0, Math.min(100, base + wave));
    points.push(headroom);
    weekMeta.push({ weekKey: key, burn, headroom });
  }
  // Ensure some variance even for empty tenants — replace flat 100s with a soft sine
  const flat = points.every((v) => v === 100);
  const series = flat
    ? Array.from({ length: POINTS }, (_, i) => 78 + 18 * Math.sin((i / POINTS) * Math.PI * 2))
    : points;
  if (flat) {
    // Keep metadata length-aligned when the demo fallback replaces the real series
    weekMeta.forEach((m, i) => { m.headroom = series[i]; });
  }

  const W = 320, H = 96, PAD_L = 6, PAD_R = 6, PAD_T = 8, PAD_B = 12;
  const FORECAST_WEEKS = 4;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;
  // Reserve trailing slots for the dashed 4-week projection so it stays inside the viewBox
  const stepX = innerW / (series.length - 1 + FORECAST_WEEKS);
  const xy = series.map((v, i) => [PAD_L + i * stepX, PAD_T + innerH - (v / 100) * innerH]);
  // Cubic-smoothed path (Catmull-Rom to bezier)
  const smooth = (() => {
    if (xy.length < 2) return "";
    let d = `M ${xy[0][0].toFixed(1)} ${xy[0][1].toFixed(1)}`;
    for (let i = 0; i < xy.length - 1; i++) {
      const p0 = xy[Math.max(0, i - 1)];
      const p1 = xy[i];
      const p2 = xy[i + 1];
      const p3 = xy[Math.min(xy.length - 1, i + 2)];
      const t = 0.22;
      const c1x = p1[0] + (p2[0] - p0[0]) * t;
      const c1y = p1[1] + (p2[1] - p0[1]) * t;
      const c2x = p2[0] - (p3[0] - p1[0]) * t;
      const c2y = p2[1] - (p3[1] - p1[1]) * t;
      d += ` C ${c1x.toFixed(1)} ${c1y.toFixed(1)}, ${c2x.toFixed(1)} ${c2y.toFixed(1)}, ${p2[0].toFixed(1)} ${p2[1].toFixed(1)}`;
    }
    return d;
  })();
  const area = smooth ? `${smooth} L ${xy[xy.length - 1][0].toFixed(1)} ${H - PAD_B} L ${xy[0][0].toFixed(1)} ${H - PAD_B} Z` : "";
  const latest = series[series.length - 1] || 0;

  // ---- 4-week dashed forecast projection ----
  // Trailing burn velocity = slope of last 4 weekly headroom points.
  // Project the trend forward with a soft mean-reversion clamp.
  const forecast = (() => {
    if (series.length < 2) return null;
    const tail = series.slice(-4); // last 4 weeks including current
    // Simple linear slope: avg(dY) across the tail
    let slope = 0;
    for (let i = 1; i < tail.length; i++) slope += tail[i] - tail[i - 1];
    slope = slope / Math.max(1, tail.length - 1);
    // Softly mean-revert (dampen extrapolation)
    slope *= 0.7;
    const lastX = xy[xy.length - 1][0];
    const lastY = xy[xy.length - 1][1];
    const projected = [];
    let val = series[series.length - 1];
    for (let i = 1; i <= FORECAST_WEEKS; i++) {
      val = Math.max(0, Math.min(100, val + slope));
      const projX = lastX + stepX * i;
      const projY = PAD_T + innerH - (val / 100) * innerH;
      projected.push({ x: projX, y: projY, v: val });
    }
    // Cap the projection line inside a reasonable X window (extend viewBox virtually)
    const path =
      `M ${lastX.toFixed(1)} ${lastY.toFixed(1)} ` +
      projected.map((p) => `L ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" ");
    return { path, projected };
  })();

  // Snapshot share handler — persists the current runway payload server-side,
  // opens the Preview Drawer so the user can confirm the render before copying
  // the signed URL or emailing it straight to their CPA.
  const handleShareSnapshot = async () => {
    if (sharing) return;
    setSharing(true);
    setEmailSentTo(null);
    setShareCopied(false);
    try {
      const forecastVals = (forecast?.projected || []).map((p) => p.v);
      const payload = buildSharePayload(
        ccy,
        series,
        forecastVals,
        latest,
        lastComputedSavedRef.current || 0
      );
      const r = await api.post("/runway/snapshot", payload);
      const relUrl = r?.data?.url;
      const token = r?.data?.token;
      const expiresAt = r?.data?.expires_at;
      const remaining = r?.data?.remaining_today;
      if (!relUrl || !token) throw new Error("no_url");
      const full = `${buildShareBaseUrl()}${relUrl}`;
      setShareUrl(full);
      setShareToken(token);
      setShareExpiresAt(expiresAt || null);
      setShareRemaining(typeof remaining === "number" ? remaining : null);
      setEmailNote(payload.note || "");
      setShowPreview(true);
      // Refresh the history strip so the new snapshot appears immediately.
      refreshSnapshotHistory();
      toast.success("Snapshot ready — preview below", {
        duration: 3500,
        id: "runway-share",
        description: "Signed SVG · valid 7 days · copy or email to your CPA.",
      });
    } catch (e) {
      const status = e?.response?.status;
      if (status === 429) {
        const info = e?.response?.data?.detail || {};
        toast.error(
          info?.reset_hint ||
            "Daily snapshot limit reached (20/day). Please try again in 24h.",
          { duration: 5000 }
        );
      } else {
        toast.error("Couldn’t generate a shareable snapshot. Please retry.", {
          duration: 4000,
        });
      }
    } finally {
      setSharing(false);
    }
  };

  const copySnapshotLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 3000);
      toast.success("Link copied", { duration: 2000, id: "runway-copy" });
      // Copy-Then-Send Analytics — record the copy on the snapshot doc so we
      // can measure copy-vs-email preference over time. Failures are silent.
      if (shareToken) {
        try {
          await api.post(`/runway/snapshot/${shareToken}/track`, { action: "copy" });
          refreshSnapshotHistory();
          refreshAnalytics();
        } catch (_) {
          /* analytics failure must never break the copy UX */
        }
      }
    } catch (_) {
      toast.error("Clipboard blocked — long-press the URL to copy it manually.", {
        duration: 3500,
      });
    }
  };

  const sendSnapshotEmail = async (e) => {
    e?.preventDefault?.();
    if (!shareToken || emailSending) return;
    const to = (emailTo || "").trim();
    // Minimal client-side sanity check; backend has the authoritative regex.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to)) {
      toast.error("Please enter a valid email address.", { duration: 3000 });
      return;
    }
    setEmailSending(true);
    try {
      const r = await api.post(`/runway/snapshot/${shareToken}/email`, {
        to_email: to,
        note: emailNote,
      });
      const data = r?.data || {};
      setEmailSentTo(to);
      setEmailRemaining(
        typeof data.remaining_sends === "number" ? data.remaining_sends : null
      );
      refreshSnapshotHistory();
      refreshAnalytics();
      if (data.simulated) {
        toast.info(`Send simulated to ${to} — Brevo not configured.`, {
          duration: 4500,
          description: "Snapshot link is still valid — copy it if you need to forward manually.",
        });
      } else if (data.sent) {
        toast.success(`Snapshot emailed to ${to}`, {
          duration: 4000,
          description: "Your CPA can view the signed link for the next 7 days.",
        });
      } else {
        toast.error("Send failed. Please retry or copy the link manually.", {
          duration: 4000,
        });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      if (status === 429 && detail && detail.error === "email_cap_per_token") {
        toast.error(detail.reset_hint || "This snapshot hit its 5-email cap.", {
          duration: 5000,
          id: "runway-email-cap",
          description: "Generate a fresh snapshot to forward to more recipients.",
        });
        setEmailRemaining(0);
      } else {
        toast.error(
          typeof detail === "string" && detail
            ? `Send failed · ${detail}`
            : "Send failed. Please retry.",
          { duration: 4000 }
        );
      }
    } finally {
      setEmailSending(false);
    }
  };

  return (
    <div
      {...tiltBind}
      data-testid="liquidity-runway-wave"
      className="glass cosmic-aura cosmic-aura-cyan cosmic-sheen rounded-2xl p-6 relative overflow-hidden"
      style={{ ...tiltBind.style, borderColor: "rgba(6,182,212,0.28)" }}
    >
      <TiltRipples />
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-cyan-300 flex items-center gap-1.5">
          <Droplet size={11} /> Liquidity Runway
        </div>
        <div
          data-testid="runway-currency-toggle"
          className="flex items-center gap-0.5 rounded-md p-0.5"
          style={{
            background: "rgba(15,23,42,0.7)",
            border: "1px solid rgba(6,182,212,0.28)",
          }}
        >
          {Object.keys(CURRENCIES).map((code) => {
            const active = ccy === code;
            return (
              <button
                key={code}
                onClick={() => setCurrency(code)}
                data-testid={`runway-currency-${code.toLowerCase()}`}
                data-active={active ? "true" : "false"}
                className={`px-2 py-0.5 rounded text-[9px] font-mono uppercase tracking-[0.2em] transition-colors ${
                  active
                    ? "text-slate-950"
                    : "text-cyan-300/70 hover:text-cyan-200"
                }`}
                style={{
                  background: active
                    ? "linear-gradient(135deg, #06B6D4, #22d3ee)"
                    : "transparent",
                  boxShadow: active
                    ? "0 6px 14px -6px rgba(6,182,212,0.7)"
                    : "none",
                }}
              >
                {code}
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex items-baseline gap-2 mb-1">
        <div data-testid="liquidity-runway-value" className="font-mono font-bold text-white text-3xl leading-none">
          {Math.round(latest)}
        </div>
        <div className="text-slate-500 font-mono text-sm">/ 100</div>
        <div className="ml-2 text-[10px] font-mono uppercase tracking-widest text-cyan-300/80">
          headroom index
        </div>
      </div>
      {(() => {
        // Marginal-rate estimated tax saved on the last 4 weeks of business burn,
        // converted into the currency the user is planning against.
        const active = CURRENCIES[ccy];
        const last4Burn = weekMeta
          .slice(-4)
          .reduce((s, m) => s + (m.burn || 0), 0);
        const saved = last4Burn * active.rate * active.fx;
        // Stash for the share handler — cheap, avoids threading yet another ref.
        lastComputedSavedRef.current = saved;
        return (
          <div
            data-testid="runway-marginal-savings"
            data-currency={ccy}
            className="mb-3 flex items-center gap-2 flex-wrap text-[10px] font-mono uppercase tracking-[0.18em]"
          >
            <span
              className="rounded-md px-2 py-0.5"
              style={{
                background:
                  "linear-gradient(135deg, rgba(6,182,212,0.14), rgba(6,182,212,0.02))",
                border: "1px solid rgba(6,182,212,0.35)",
                color: "#67e8f9",
              }}
            >
              ≈ {active.symbol}
              {saved.toLocaleString(undefined, {
                maximumFractionDigits: 0,
              })}{" "}
              marg. save · {Math.round(active.rate * 100)}%
            </span>
            <span className="text-slate-500 normal-case tracking-normal text-[10px]">
              {active.label} · 4w
              {liveFx && (
                <span
                  data-testid="fx-live-indicator"
                  className="ml-2 inline-flex items-center gap-1 text-emerald-400/80"
                  title={
                    fxUpdatedAt
                      ? `Live FX · updated ${new Date(fxUpdatedAt).toLocaleTimeString()}`
                      : "Live FX active"
                  }
                >
                  <span
                    className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400"
                    style={{ boxShadow: "0 0 8px rgba(16,185,129,0.9)" }}
                  />
                  live fx
                </span>
              )}
            </span>
            {analytics && (analytics.total_copies + analytics.total_sends) > 0 && (() => {
              const c = analytics.total_copies || 0;
              const s = analytics.total_sends || 0;
              const total = c + s;
              const copyPct = total ? Math.round((c / total) * 100) : 0;
              const sendPct = 100 - copyPct;
              return (
                <span
                  data-testid="runway-analytics-chip"
                  className="ml-1 inline-flex items-center gap-1 rounded-md px-1.5 py-0.5"
                  title={`Last 30d — ${c} copies · ${s} sends · ${analytics.total_snapshots || 0} snapshots`}
                  style={{
                    background:
                      "linear-gradient(90deg, rgba(6,182,212,0.16) 0%, rgba(6,182,212,0.16) " +
                      copyPct +
                      "%, rgba(16,185,129,0.18) " +
                      copyPct +
                      "%, rgba(16,185,129,0.18) 100%)",
                    border: "1px solid rgba(148,163,184,0.25)",
                    color: "#e2e8f0",
                    fontSize: 9,
                    letterSpacing: "1px",
                  }}
                >
                  <span className="text-cyan-300">c{c}</span>
                  <span className="text-slate-500">·</span>
                  <span className="text-emerald-300">s{s}</span>
                  <span className="text-slate-500 normal-case tracking-normal">
                    ({copyPct}/{sendPct})
                  </span>
                </span>
              );
            })()}
            <button
              onClick={handleShareSnapshot}
              disabled={sharing}
              data-testid="runway-share-btn"
              className="ml-auto inline-flex items-center gap-1 rounded-md px-2 py-1 text-[9px] font-mono uppercase tracking-[0.2em] transition-colors"
              style={{
                background: shareUrl
                  ? "linear-gradient(135deg, rgba(16,185,129,0.22), rgba(16,185,129,0.06))"
                  : "linear-gradient(135deg, rgba(6,182,212,0.18), rgba(6,182,212,0.04))",
                border: `1px solid ${shareUrl ? "rgba(16,185,129,0.55)" : "rgba(6,182,212,0.45)"}`,
                color: shareUrl ? "#6ee7b7" : "#67e8f9",
                boxShadow: shareUrl
                  ? "0 8px 18px -8px rgba(16,185,129,0.55)"
                  : "0 8px 18px -8px rgba(6,182,212,0.55)",
                opacity: sharing ? 0.6 : 1,
              }}
            >
              {shareCopied ? (
                <>
                  <Check size={10} /> copied
                </>
              ) : (
                <>
                  <Share2 size={10} /> {shareUrl ? "share ready" : "share to cpa"}
                </>
              )}
            </button>
          </div>
        );
      })()}
      <SnapshotHistoryStrip
        history={snapshotHistory}
        pinA={pinA}
        pinB={pinB}
        onPin={pinSnapshot}
        onClear={clearPins}
        onOpenComparison={() => setShowComparison(true)}
        onRestore={restoreSnapshot}
        formatCountdown={formatCountdown}
      />
      <RunwayComparisonOverlay
        open={showComparison}
        pinA={pinA}
        pinB={pinB}
        buildShareBaseUrl={buildShareBaseUrl}
        onClose={() => setShowComparison(false)}
      />
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} className="liquidity-wave-svg" preserveAspectRatio="none">
        <defs>
          <linearGradient id="liquidity-stroke" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#06B6D4" />
            <stop offset="100%" stopColor="#F59E0B" />
          </linearGradient>
          <linearGradient id="liquidity-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#06B6D4" stopOpacity="0.28" />
            <stop offset="100%" stopColor="#F59E0B" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="liquidity-danger" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#f43f5e" stopOpacity="0.28" />
            <stop offset="100%" stopColor="#f43f5e" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {/* Downside danger ribbon — everything below 40 headroom */}
        {(() => {
          const DANGER_THRESHOLD = 40;
          const dy = PAD_T + innerH - (DANGER_THRESHOLD / 100) * innerH;
          return (
            <g data-testid="liquidity-danger-ribbon" className="liquidity-danger-ribbon">
              <rect
                x={PAD_L} y={dy}
                width={innerW}
                height={Math.max(0, (H - PAD_B) - dy)}
                fill="url(#liquidity-danger)"
              />
              <line
                x1={PAD_L} x2={PAD_L + innerW}
                y1={dy} y2={dy}
                stroke="#f43f5e"
                strokeOpacity="0.45"
                strokeWidth="0.75"
                strokeDasharray="2 4"
              />
              <text
                x={PAD_L + 4}
                y={dy - 3}
                fontSize="7"
                fontFamily="ui-monospace, monospace"
                fill="#fda4af"
                opacity="0.9"
                style={{ letterSpacing: "0.14em", textTransform: "uppercase" }}
              >
                Danger &lt; 40
              </text>
            </g>
          );
        })()}
        <path d={area} fill="url(#liquidity-fill)" className="liquidity-wave-area" />
        <path
          d={smooth}
          fill="none"
          stroke="url(#liquidity-stroke)"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="liquidity-wave-path"
          style={{ filter: "drop-shadow(0 0 6px rgba(6,182,212,0.45))" }}
        />
        {/* Dashed 4-week forecast projection — same amber tone as endpoint marker */}
        {forecast && (
          <>
            <path
              data-testid="liquidity-forecast-line"
              d={forecast.path}
              fill="none"
              stroke="#F59E0B"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeDasharray="4 5"
              opacity="0.85"
              style={{ filter: "drop-shadow(0 0 4px rgba(245,158,11,0.5))" }}
              className="liquidity-forecast-path"
            />
            {forecast.projected.map((p, i) => (
              <circle key={`fc-${i}`} cx={p.x} cy={p.y}
                      r={i === forecast.projected.length - 1 ? 2.6 : 1.4}
                      fill="#0f172a" stroke="#F59E0B" strokeWidth="1.1"
                      opacity="0.85" />
            ))}
          </>
        )}
        {xy.map((p, i) => (
          <circle key={i} cx={p[0]} cy={p[1]}
                  r={i === xy.length - 1 ? 3.2 : 1.6}
                  fill={i === xy.length - 1 ? "#F59E0B" : "#0f172a"}
                  stroke={i === xy.length - 1 ? "#F59E0B" : "#06B6D4"}
                  strokeWidth="1.1" />
        ))}
        {/* Invisible larger hit-targets — 10px radius per node for comfortable hover */}
        {xy.map((p, i) => (
          <circle
            key={`hit-${i}`}
            data-testid={`liquidity-node-${i}`}
            cx={p[0]} cy={p[1]} r={10}
            fill="transparent"
            style={{ cursor: "pointer" }}
            onMouseEnter={() => setHoverIdx(i)}
            onMouseLeave={() => setHoverIdx(null)}
          />
        ))}
      </svg>
      {/* Hover tooltip — floats above the SVG, keyed to the hovered node index */}
      {hoverIdx !== null && weekMeta[hoverIdx] && (() => {
        const meta = weekMeta[hoverIdx];
        // Position tooltip based on the node's fractional x/y inside the SVG.
        const leftPct = (xy[hoverIdx][0] / W) * 100;
        const topPct = (xy[hoverIdx][1] / H) * 100;
        // Anchor: below the point when the point is high, above when low
        const above = xy[hoverIdx][1] > H * 0.55;
        const weekLabel = (() => {
          try {
            return new Date(meta.weekKey).toLocaleDateString(undefined, {
              month: "short", day: "numeric",
            });
          } catch { return meta.weekKey; }
        })();
        return (
          <div
            data-testid="liquidity-tooltip"
            className="absolute pointer-events-none z-10 -translate-x-1/2 rounded-lg px-3 py-2 text-[11px] font-mono shadow-2xl"
            style={{
              left: `calc(${leftPct}% * (100% - 48px) / 100% + 24px)`,
              top: above ? `calc(${topPct}% - 66px)` : `calc(${topPct}% + 16px)`,
              background: "linear-gradient(180deg, rgba(15,23,42,0.98), rgba(2,6,23,0.98))",
              border: "1px solid rgba(6,182,212,0.5)",
              boxShadow: "0 12px 28px -10px rgba(6,182,212,0.55)",
              minWidth: 140,
            }}
          >
            <div className="text-[9px] uppercase tracking-widest text-cyan-300/80 mb-0.5">
              Week of {weekLabel}
            </div>
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-slate-400">Headroom</span>
              <span className="text-cyan-300 font-semibold">
                {Math.round(meta.headroom)}<span className="text-slate-500">/100</span>
              </span>
            </div>
            <div className="flex items-baseline justify-between gap-3 mt-0.5">
              <span className="text-slate-400">Burn</span>
              <span className="text-amber-300 font-semibold">
                ${meta.burn.toFixed(2)}
              </span>
            </div>
          </div>
        );
      })()}
      <div className="mt-1 flex items-center justify-between text-[9px] font-mono uppercase tracking-widest text-slate-600">
        <span>12w ago</span>
        <span>now</span>
        <span className="text-amber-400/70" data-testid="liquidity-forecast-label">+4w forecast</span>
      </div>
      <style>{`
        .liquidity-wave-path {
          stroke-dasharray: 1200;
          stroke-dashoffset: 1200;
          animation: liquidity-draw 1600ms cubic-bezier(0.22,1,0.36,1) forwards;
          transform-origin: center;
        }
        .liquidity-wave-area {
          opacity: 0;
          animation: liquidity-area-fade 1400ms ease-in-out 300ms forwards;
        }
        .liquidity-forecast-path {
          stroke-dashoffset: 200;
          animation: liquidity-forecast-draw 900ms ease-in-out 1600ms forwards;
        }
        .liquidity-danger-ribbon {
          opacity: 0;
          animation: liquidity-danger-fade 900ms ease-in-out 500ms forwards;
        }
        @keyframes liquidity-danger-fade {
          0%   { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes liquidity-forecast-draw {
          0%   { stroke-dashoffset: 200; opacity: 0; }
          100% { stroke-dashoffset: 0;   opacity: 0.85; }
        }
        @keyframes liquidity-draw {
          0%   { stroke-dashoffset: 1200; }
          100% { stroke-dashoffset: 0; }
        }
        @keyframes liquidity-area-fade {
          0%   { opacity: 0; }
          100% { opacity: 1; }
        }
      `}</style>
      <SnapshotPreviewDrawer
        open={showPreview}
        shareUrl={shareUrl}
        shareRemaining={shareRemaining}
        shareExpiresAt={shareExpiresAt}
        shareCopied={shareCopied}
        onClose={() => setShowPreview(false)}
        onCopy={copySnapshotLink}
        emailTo={emailTo}
        setEmailTo={setEmailTo}
        emailNote={emailNote}
        setEmailNote={setEmailNote}
        emailSending={emailSending}
        emailSentTo={emailSentTo}
        emailRemaining={emailRemaining}
        onSendEmail={sendSnapshotEmail}
      />
    </div>
  );
}


